import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Loader2, UserRound } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

/**
 * Reusable avatar/logo uploader. Posts the file to /api/profile/photo (which stores it
 * in object storage and attaches the returned URL to the current user's profile).
 */
export const ProfilePhotoUpload = ({
  currentUrl,
  onUploaded,
  label = 'Profile photo',
  hint,
  shape = 'circle',
  testId = 'profile-photo',
}) => {
  const { getAuthHeader, setUser } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // `preview` holds an optimistic / just-uploaded URL; otherwise we render the currentUrl prop.
  const [preview, setPreview] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      toast.error('Please choose a JPG, PNG or WebP image.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image is too large. Max size is 5 MB.');
      return;
    }
    const localUrl = URL.createObjectURL(f);
    setPreview(localUrl);
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await axios.post(`${API}/profile/photo`, form, {
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data.photo_url;
      setPreview(url);
      onUploaded?.(url);
      // Keep the logged-in user's avatar (top-bar) in sync immediately.
      setUser?.((u) => (u ? { ...u, photo_url: url } : u));
      toast.success('Photo updated');
    } catch (err) {
      setPreview(null);
      toast.error(err.response?.data?.detail || 'Could not upload the photo.');
    } finally {
      setUploading(false);
      URL.revokeObjectURL(localUrl);
    }
  };

  const rounded = shape === 'circle' ? 'rounded-full' : 'rounded-2xl';

  return (
    <div className="flex items-center gap-4" data-testid={testId}>
      <div className={`relative w-20 h-20 ${rounded} overflow-hidden bg-cn-bone-100 dark:bg-white/5 border border-cn-border flex items-center justify-center flex-shrink-0`}>
        {(preview || currentUrl) ? (
          <img src={preview || currentUrl} alt={label} className="w-full h-full object-cover" data-testid={`${testId}-img`} />
        ) : (
          <UserRound className="w-8 h-8 text-cn-text-muted" />
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          </div>
        )}
      </div>
      <div>
        <p className="font-display font-bold text-cn-text dark:text-white">{label}</p>
        {hint && <p className="text-sm text-cn-text-muted mt-0.5 max-w-xs">{hint}</p>}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="cn-btn cn-btn-primary text-sm mt-2 disabled:opacity-60"
          data-testid={`${testId}-btn`}
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          {(preview || currentUrl) ? 'Change photo' : 'Upload photo'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={onFile}
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
};

export default ProfilePhotoUpload;
